// Chamadas dos follow-ups do pipeline privado (actions `fu_*` em
// /comercial/propostas-geradas). Doc: docs/followups-propostas.md.
// Uma função por ação, todas devolvendo o corpo já aberto; o erro vem com a
// frase do servidor (`erroDe`), que é o que a tela mostra.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

async function post<T = any>(body: Record<string, any>, timeout = 25000): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, body, { headers: { Authorization: token }, timeout });
  return (r.data?.body ? JSON.parse(r.data.body) : r.data) as T;
}

export const erroDe = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.erro || e?.message || 'erro desconhecido';

export type Modo = 'desligado' | 'manual' | 'automatico';

export interface FollowupConfig {
  primeiro_apos_dias: number;
  intervalo_dias: number;
  max_tentativas: number;
  modo_padrao: Modo;
  envio_automatico_ativo: boolean;
  janela_dias_semana: number[];
  janela_hora_inicio: number;
  janela_hora_fim: number;
  template_assunto: string;
  template_corpo: string;
  copia_remetente: boolean;
  perda_automatica: boolean;
  perder_apos_dias: number;
  aviso_perda_dias: number;
  canal: 'sistema' | 'outlook';
  atualizado_em: string | null;
  atualizado_por: string | null;
  ultima_rodada_em: string | null;
  ultima_rodada: ResumoRodada | null;
}

export interface ResumoRodada {
  sucesso: boolean;
  dry_run?: boolean;
  hoje: string;
  janela_aberta: boolean;
  inscritos: number;
  tarefas: number;
  contadas: number;
  enviados: number;
  falhas: number;
  perdidos: number;
  avisos_perda: number;
  incertos?: number;
  sairam?: number;
  voltaram?: number;
  ocupado?: boolean;
  total?: number;
  itens?: { id: number; titulo: string; modo: Modo; acao: string; motivo?: string; erro?: string; para?: string }[];
}

export interface Placeholder { chave: string; descricao: string }

export interface Situacao { codigo: string; rotulo: string; data?: string | null }

export interface Remetente { email: string; nome: string; origem: 'vendedor_email' | 'nome'; exibido?: string }

export interface ContatoOpcao { id: number; nome: string; email: string; cargo: string | null }

export interface ItemFila {
  id: number;
  titulo: string;
  cliente: string | null;
  cliente_id: number | null;
  valor_estimado: string | number | null;
  responsavel: string | null;
  remetente: Remetente | null;
  contato_nome: string | null;
  contato_email: string | null;
  contato_id: number | null;
  contatos_do_cliente: ContatoOpcao[];
  numero_proposta: string | null;
  data_envio: string | null;
  legado: boolean;
  modo: Modo | null;
  tentativas: number;
  proximo_em: string | null;
  ultimo_followup_em: string | null;
  pausado_ate: string | null;
  respondido_em: string | null;
  situacao: Situacao;
  email_bloqueado_por: string[];
}

export interface Envio {
  id: number;
  proposta_id: number;
  tentativa: number;
  tipo: 'email' | 'tarefa';
  status: 'enviando' | 'enviado' | 'falhou' | 'incerto' | 'criada' | 'cancelada';
  canal: string | null;
  remetente: string | null;
  destinatarios: string[] | null;
  assunto: string | null;
  corpo: string | null;
  erro: string | null;
  atividade_id: string | null;
  respondido_em: string | null;
  resposta_resumo: string | null;
  criado_em: string;
  titulo?: string;
  status_negocio?: string;
  cliente?: string | null;
}

export interface Previa {
  vazio?: boolean;
  negocio?: { id: number; titulo: string; cliente: string | null };
  remetente: Remetente | null;
  destinatario: string | null;
  assunto: string;
  texto: string;
  html: string;
  faltando: string[];
  desconhecidos: string[];
  bloqueado_por: string[];
}

export interface DoNegocio {
  em_followup: boolean;
  status: string;
  estado: null | {
    modo: Modo; tentativas: number; proximo_em: string | null; ancora_em: string | null;
    ultimo_followup_em: string | null; pausado_ate: string | null; respondido_em: string | null; legado: boolean;
  };
  situacao: Situacao;
  max_tentativas: number;
  envio_automatico_ativo: boolean;
  remetente: Remetente | null;
  contato_email: string | null;
  envios: Envio[];
}

export interface ItemLimpeza {
  id: number;
  titulo: string;
  responsavel: string | null;
  valor_estimado: string | number | null;
  data_envio: string | null;
  cliente: string | null;
  contato_email: string | null;
  ultimo_movimento: string | null;
  dias_parado: number;
  do_ploomes: boolean;
  sugestao: 'perder' | 'manual';
}

export const fu = {
  config: () => post<{ config: FollowupConfig; placeholders: Placeholder[]; pode_configurar: boolean; motivo_perda: string }>({ action: 'fu_config' }),
  salvarConfig: (config: Partial<FollowupConfig>) => post<{ config: FollowupConfig }>({ action: 'fu_config_salvar', config }),
  previa: (body: { proposta_id?: number; assunto?: string; corpo?: string }) => post<Previa>({ action: 'fu_previa', ...body }),
  teste: (body: { proposta_id?: number; assunto?: string; corpo?: string }) =>
    post<{ enviado_para: string; bloqueado_por: string[] }>({ action: 'fu_teste', ...body }),
  fila: () => post<{ hoje: string; config: Pick<FollowupConfig, 'max_tentativas' | 'envio_automatico_ativo' | 'perda_automatica' | 'ultima_rodada_em' | 'ultima_rodada'>; itens: ItemFila[] }>({ action: 'fu_fila' }),
  negocio: (proposta_id: number) => post<DoNegocio>({ action: 'fu_negocio', proposta_id }),
  definir: (proposta_id: number, mudanca: { modo?: Modo; pausado_ate?: string | null }) =>
    post<DoNegocio>({ action: 'fu_definir', proposta_id, ...mudanca }),
  respondeu: (proposta_id: number, resumo: string, pausar_ate: string | null) =>
    post<DoNegocio>({ action: 'fu_respondeu', proposta_id, resumo, pausar_ate }),
  enviados: (dias: number, tipo?: 'email' | 'tarefa') => post<{ itens: Envio[] }>({ action: 'fu_enviados', dias, tipo }),
  limpezaPrevia: (dias_sem_movimento: number) =>
    post<{ dias_sem_movimento: number; itens: ItemLimpeza[]; resumo: { total: number; perder: number; valor_perder: number; manter: number; valor_manter: number } }>(
      { action: 'fu_limpeza_previa', dias_sem_movimento }),
  limpezaAplicar: (acoes: { proposta_id: number; acao: 'perder' | Modo }[]) =>
    post<{ perdidos: number; ligados: number; mantidos: number; ignorados: number }>({ action: 'fu_limpeza_aplicar', acoes }, 60000),
  rodar: (dry_run: boolean) => post<ResumoRodada & { status?: string; mensagem?: string }>({ action: 'fu_rodar', dry_run }, 28000),
};
